import api from '_server_utils/api'
import { urlWithQueryParams } from '_server_utils/utils'

const DPOINBOX_API_URL = process.env.DPOINBOX_API_URL

const get = (path, token) => api(`${DPOINBOX_API_URL}${path}`, token).get()
const post = (path, payload, token, customOptions) =>
  api(`${DPOINBOX_API_URL}${path}`, token).post(payload, customOptions)
const patch = (path, payload, token) =>
  api(`${DPOINBOX_API_URL}${path}`, token).patch(payload)
const put = (path, payload, token) =>
  api(`${DPOINBOX_API_URL}${path}`, token).put(payload)
const destroy = (path, token) =>
  api(`${DPOINBOX_API_URL}${path}`, token).destroy()

/**
 * Authentication
 */

export const login = async (payload) => post('api/token/', payload)

export const verifyToken = async (payload) => post('api/token/verify/', payload)

export const refreshToken = async (payload) =>
  post('api/token/refresh/', payload)
/**
 * Assessment Templates
 */

//  Templates
export const getAssessments = async (token) => get('api/temps/', token)

export const getAssessment = async (id, params, token) => {
  const url = `api/temps/${id}/`
  return get(urlWithQueryParams(url, params), token)
}

export const createAssessment = async (payload, token) =>
  post('api/temps/', payload, token)

export const updateAssessment = async (id, payload, token) =>
  patch(`api/temps/${id}/`, payload, token)

//  Sections
export const getAssessmentSections = async (assessmentId, params, token) => {
  const url = `api/templates/temp/${assessmentId}/sects/`
  return get(urlWithQueryParams(url, params), token)
}

export const createAssessmentSection = async (payload, token) =>
  post('api/templates/sect/', payload, token)

export const updateAssessmentSection = async (id, payload, token) =>
  patch(`api/templates/sect/${id}/`, payload, token)

//  Questions
export const getAssessmentQuestionsBySection = async (
  sectionIds,
  params,
  token
) =>
  Promise.all(
    sectionIds.map((sectionId) => {
      let url = `api/templates/sect/${sectionId}/quests/`
      return get(urlWithQueryParams(url, params), token)
    })
  )

export const getAssessmentQuestionsByAssessment = async (
  assessmentId,
  token,
  params
) =>
  get(
    urlWithQueryParams(`api/templates/temp/${assessmentId}/quests/`, params),
    token
  )

export const createAssessmentQuestion = async (payload, token) =>
  post('api/templates/quest/', payload, token)

export const updateAssessmentQuestion = async (id, payload, token) =>
  patch(`api/templates/quest/${id}/`, payload, token)

export const deleteAssessmentQuestion = async (id, token) =>
  destroy(`api/templates/quest/${id}/`, token)

//  Answers
export const getAssessmentAnswersByQuestion = async (
  questionIds,
  params,
  token
) =>
  Promise.all(
    questionIds.map((questionId) => {
      let url = `api/templates/quest/${questionId}/ans/`
      return get(urlWithQueryParams(url, params), token)
    })
  )

export const getAssessmentAnswersByAssessment = async (
  assessmentId,
  token,
  params
) =>
  get(
    urlWithQueryParams(`api/templates/temp/${assessmentId}/ans/`, params),
    token
  )

export const createAssessmentAnswer = async (payload, token) =>
  post('api/templates/ans/', payload, token)

export const batchCreateAssessmentAnswers = async (payloads, token) =>
  Promise.all(payloads.map((payload) => createAssessmentAnswer(payload, token)))

export const updateAssessmentAnswer = async (id, payload, token) =>
  put(`api/templates/ans/${id}/`, payload, token)

export const deleteAssessmentAnswer = async (id, token) =>
  destroy(`api/templates/ans/${id}/`, token)

//  Versions
const updateAssessmentVersion = async (payload, token) =>
  post('api/templates/vers/update/', payload, token)

export const publishAssessmentVersion = async (assessmentId, token) =>
  updateAssessmentVersion({ action: 'publish', temp: assessmentId }, token)

export const createAssessmentVersion = async (assessmentId, token) =>
  updateAssessmentVersion({ action: 'new', temp: assessmentId }, token)

/**
 * CA Assessment Templates
 */

// Templates
export const getCaAssessments = async (token) => get('api/ca-temps/', token)

export const getCaAssessment = async (id, token) =>
  get(`api/ca-temps/${id}/`, token)

export const createCaAssessment = async (payload, token) =>
  post('api/ca-temps/', payload, token)

export const updateCaAssessment = async (id, payload, token) =>
  patch(`api/ca-temps/${id}/`, payload, token)

// Sections
export const getCaAssessmentSections = async (assessmentId, token) =>
  get(`api/ca-templates/temp/${assessmentId}/sects/`, token)

export const createCaAssessmentSection = async (payload, token) =>
  post('api/ca-templates/sect/', payload, token)

export const updateCaAssessmentSection = async (id, payload, token) =>
  patch(`api/ca-templates/sect/${id}/`, payload, token)

// Questions
export const getCaAssessmentQuestionsBySection = async (sectionIds, token) =>
  Promise.all(
    sectionIds.map((sectionId) =>
      get(`api/ca-templates/sect/${sectionId}/quests/`, token)
    )
  )

export const getCaAssessmentQuestionsByCaAssessment = async (
  assessmentId,
  token
) => get(`api/ca-templates/temp/${assessmentId}/quests/`, token)

export const createCaAssessmentQuestion = async (payload, token) =>
  post('api/ca-templates/quest/', payload, token)

export const updateCaAssessmentQuestion = async (id, payload, token) =>
  patch(`api/ca-templates/quest/${id}/`, payload, token)

export const deleteCaAssessmentQuestion = async (id, token) =>
  destroy(`api/ca-templates/quest/${id}/`, token)

// Answers
export const getCaAssessmentAnswersByQuestions = async (questionIds, token) =>
  Promise.all(
    questionIds.map((questionId) =>
      get(`api/ca-templates/quest/${questionId}/ans/`, token)
    )
  )

export const getCaAssessmentAnswersByCaAssessment = async (
  assessmentId,
  token
) => get(`api/ca-templates/temp/${assessmentId}/ans/`, token)

export const createCaAssessmentAnswer = async (payload, token) =>
  post('api/ca-templates/ans/', payload, token)

export const batchCreateCaAssessmentAnswers = async (payloads, token) =>
  Promise.all(
    payloads.map((payload) => createCaAssessmentAnswer(payload, token))
  )

export const updateCaAssessmentAnswer = async (id, payload, token) =>
  patch(`api/ca-templates/ans/${id}/`, payload, token)

export const deleteCaAssessmentAnswer = async (id, token) =>
  destroy(`api/ca-templates/ans/${id}/`, token)

/**
 * Pol Gen
 */

export const getPolicyBlock = async (payload, token) =>
  post('api/policies/block/', payload, token)

export const createPolicyLogic = async (payload, token) =>
  post('api/policies/logic/', payload, token)
